<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
  import {
    computed,
    defineAsyncComponent,
    ref,
    onMounted,
    onUnmounted,
    onBeforeMount,
    getCurrentInstance,
  } from 'vue';
  import {useThemeColors} from '@core/composables/useThemeColors';
  import ConfigProvider from '@core/services/ConfigProvider';
  import {getComponentConfig} from '@core/utils/getComponentConfig';
  import {getComponentPath} from '@core/utils/getComponentPath';
  import {getLucideIconComponent} from '@core/utils/lucideIcons';
  import type {LucideIconName} from '@core/utils/lucideIcons';

  defineOptions({
    inheritAttrs: false,
  });

  type ClassValue =
    | string
    | Record<string, boolean>
    | Array<string | Record<string, boolean>>;

  type IconProps =
    | {
        name: LucideIconName;
        set?: 'lucide';
      }
    | {
        name: string;
        set?: 'svg';
      };

  type BaseProps = {
    display?: 'inline' | 'block';
    start?: boolean;
    end?: boolean;
    ariaLabel?: string;
    ariaHidden?: boolean | string;
    role?: string;
    size?:
      | 'x-small'
      | 'small'
      | 'default'
      | 'medium'
      | 'large'
      | 'x-large'
      | 'xx-large'
      | number
      | string;
    color?: string;
    class?: ClassValue;
  };

  const props = withDefaults(defineProps<IconProps & BaseProps>(), {
    set: 'lucide',
    display: 'inline',
    start: false,
    end: false,
    ariaLabel: '',
    ariaHidden: undefined,
    role: '',
    size: 'default',
    color: '',
    class: '',
  });

  // Helper to normalize class values (string, object, or array) into a string array
  const normalizeClass = (value: ClassValue): string[] => {
    if (!value) return [];
    if (typeof value === 'string') {
      return value.split(' ').filter(Boolean);
    }
    if (Array.isArray(value)) {
      return value.flatMap(v => normalizeClass(v));
    }
    if (typeof value === 'object') {
      return Object.entries(value)
        .filter(([, active]) => active)
        .map(([className]) => className);
    }
    return [];
  };

  const theme = useThemeColors();

  // Lazy load all SVG icons for fallback support
  const svgIcons = import.meta.glob('../../assets/**/*.svg', {
    eager: false,
    import: 'default',
  });

  const svgAsyncCache = new Map<
    string,
    ReturnType<typeof defineAsyncComponent>
  >();

  const warnedMissingSvgPaths = new Set<string>();

  function getCachedSvgAsync(pathKey: string) {
    const loader = svgIcons[pathKey];
    if (!loader) return null;
    let cached = svgAsyncCache.get(pathKey);
    if (!cached) {
      cached = defineAsyncComponent(() => loader() as Promise<any>);
      svgAsyncCache.set(pathKey, cached);
    }
    return cached;
  }

  // Default config - can be overridden per project
  const defaultConfig = {
    base: 'inline-flex items-center justify-center',
    display: {
      inline: 'inline-flex align-middle',
      block: 'flex w-full',
    },
    sizes: {
      'x-small': 'w-4 h-4',
      small: 'w-5 h-5',
      default: 'w-6 h-6',
      large: 'w-7 h-7',
      'x-large': 'w-8 h-8',
      'xx-large': 'w-16 h-16',
    },
    // Pixel values for icon size props
    sizeValues: {
      'x-small': 16,
      small: 20,
      default: 24,
      large: 28,
      'x-large': 32,
      'xx-large': 64,
    },
    positioning: {
      start: {
        ltr: 'ms-2',
        rtl: 'me-2',
      },
      end: {
        ltr: 'me-2',
        rtl: 'ms-2',
      },
    },
    svg: {
      base: 'fill-current',
    },
  };

  const config = ref(defaultConfig);

  onBeforeMount(async () => {
    config.value = await getComponentConfig('Icon', defaultConfig);
  });

  /** `medium` is an alias of `default` (same token map). */
  const resolveNamedSizeKey = (size: string): string =>
    size === 'medium' ? 'default' : size;

  // RTL/LTR direction detection (per-instance observer — not global)
  const direction = ref<'ltr' | 'rtl'>('ltr');
  const directionObserver = ref<MutationObserver | null>(null);
  const updateDirection = () => {
    const dir =
      document.documentElement.getAttribute('dir') ||
      document.documentElement.dir ||
      'ltr';
    direction.value = (dir === 'rtl' ? 'rtl' : 'ltr') as 'ltr' | 'rtl';
  };

  onMounted(() => {
    updateDirection();
    const observer = new MutationObserver(updateDirection);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['dir'],
    });
    directionObserver.value = observer;
  });

  onUnmounted(() => {
    directionObserver.value?.disconnect();
    directionObserver.value = null;
  });

  // Helper to get Lucide icons from the static registry.
  // This approach enables tree-shaking - only icons in the registry are bundled.
  const getLucideIcon = (name: string) => {
    const trimmedName = name.trim();
    const icon = getLucideIconComponent(trimmedName);

    if (!icon) {
      // Warn in development mode when an icon is not in the registry
      if (import.meta.env.DEV) {
        const instance = getCurrentInstance();
        const pathString = getComponentPath(instance);

        console.warn(
          `Lucide icon "${trimmedName}" not found in registry (path: ${pathString}). ` +
            `Add it to src/core/utils/lucideIcons.ts to use it.`,
        );
      }
      return null;
    }

    return icon;
  };

  // Get Lucide icon component
  const lucideIcon = computed(() => {
    if (props.set === 'lucide' && props.name) {
      return getLucideIcon(props.name);
    }
    return null;
  });

  // Get SVG icon component with client-specific fallback support (lazy loaded)
  const svgIcon = computed(() => {
    if (props.set === 'svg' && props.name) {
      const svgPath = props.name.trim();
      const client = ConfigProvider.value('client') || 'default';

      // Try client-specific path first
      if (client && client !== 'default') {
        const clientPath = `../../assets/${client}/${svgPath}.svg`;
        const fromClient = getCachedSvgAsync(clientPath);
        if (fromClient) return fromClient;
      }

      // Fall back to default path
      const defaultPath = `../../assets/${svgPath}.svg`;
      const fromDefault = getCachedSvgAsync(defaultPath);
      if (fromDefault) return fromDefault;
      if (import.meta.env.DEV) {
        const warnKey = `${client}|${svgPath}`;
        if (!warnedMissingSvgPaths.has(warnKey)) {
          warnedMissingSvgPaths.add(warnKey);
          const instance = getCurrentInstance();
          const pathString = getComponentPath(instance);
          console.warn(
            `[Icon] SVG not found: "${svgPath}" (client=${client}). ${pathString}`,
          );
        }
      }
      return null;
    }
    return null;
  });

  // Resolve size to number (for icon components)
  const iconSizeNumber = computed(() => {
    if (typeof props.size === 'number') {
      return props.size;
    }
    const sizeKey = resolveNamedSizeKey(
      props.size as string,
    ) as keyof typeof defaultConfig.sizeValues;
    return config.value.sizeValues[sizeKey] || config.value.sizeValues.default;
  });

  // Resolve color with dark/light mode support
  const iconColor = computed(() => {
    if (!props.color) {
      return '';
    }
    const themeColors = theme.colors.value;
    if (themeColors && props.color in themeColors) {
      return `var(--${props.color})`;
    }
    return props.color;
  });

  // Build wrapper classes from config
  const wrapperClasses = computed(() => {
    const classes: string[] = ['icon', config.value.base];

    // Display mode
    const displayClass = config.value.display[props.display];
    if (displayClass) {
      classes.push(displayClass);
    }

    // Size classes (only for named sizes)
    if (typeof props.size === 'string') {
      const sizeKey = resolveNamedSizeKey(
        props.size,
      ) as keyof typeof defaultConfig.sizes;
      const sizeClass = config.value.sizes[sizeKey];
      if (sizeClass) {
        classes.push(sizeClass);
      }
    }

    // Positioning classes
    if (props.start) {
      const startClass = config.value.positioning.start[direction.value];
      if (startClass) {
        classes.push(startClass);
      }
    }
    if (props.end) {
      const endClass = config.value.positioning.end[direction.value];
      if (endClass) {
        classes.push(endClass);
      }
    }

    // User-provided classes (supports string, object, or array)
    if (props.class) {
      classes.push(...normalizeClass(props.class));
    }

    return classes.join(' ');
  });

  // SVG-specific classes
  const svgClasses = computed(() => {
    return config.value.svg.base;
  });

  // Custom size style (for numeric or custom string sizes)
  const customSizeStyle = computed(() => {
    if (typeof props.size === 'number') {
      return {
        width: `${props.size}px`,
        height: `${props.size}px`,
      };
    }
    // Check if it's a custom string size (not in our size map)
    if (
      typeof props.size === 'string' &&
      !(resolveNamedSizeKey(props.size) in config.value.sizes)
    ) {
      return {
        width: props.size,
        height: props.size,
      };
    }
    return {};
  });

  // Color style
  const colorStyle = computed(() => {
    if (iconColor.value) {
      return {color: iconColor.value};
    }
    return {};
  });

  // Combined styles
  const wrapperStyle = computed(() => {
    return {
      ...customSizeStyle.value,
      ...colorStyle.value,
    };
  });

  // Accessibility attributes
  const ariaAttributes = computed(() => {
    const attrs: Record<string, string | boolean> = {};
    const isDecorative = !props.ariaLabel;

    // ariaHidden may arrive as a real boolean or as a string literal
    // (e.g. when written as the HTML attribute `aria-hidden="true"`).
    const normalizedAriaHidden =
      typeof props.ariaHidden === 'string'
        ? props.ariaHidden !== 'false'
        : props.ariaHidden;

    attrs['aria-hidden'] =
      normalizedAriaHidden === undefined ? isDecorative : normalizedAriaHidden;

    if (props.ariaLabel) {
      attrs['aria-label'] = props.ariaLabel;
    }

    if (props.role) {
      attrs.role = props.role;
    } else if (props.ariaLabel) {
      attrs.role = 'img';
    }

    return attrs;
  });
</script>

<template>
  <!-- Lucide Icon (default) -->
  <span
    v-if="set === 'lucide' && lucideIcon"
    :class="wrapperClasses"
    :style="wrapperStyle"
    v-bind="{...$attrs, ...ariaAttributes}">
    <component
      :is="lucideIcon"
      :size="iconSizeNumber"
      :color="iconColor || undefined" />
  </span>

  <!-- SVG Icon -->
  <span
    v-else-if="set === 'svg' && svgIcon"
    :class="wrapperClasses"
    :style="wrapperStyle"
    v-bind="{...$attrs, ...ariaAttributes}">
    <component
      :is="svgIcon"
      :class="svgClasses" />
  </span>
</template>
